/**
 * PQCD 지표 설명.
 *
 * 화면에 숫자만 있으면 "이게 뭘 재는 건지", "어떻게 나온 값인지" 를 알 수 없다.
 * 카드마다 물음표를 달아 여기 적어둔 설명을 띄운다.
 *
 *   P  Product    생산(Product) — 무엇을, 얼마나 만들었나
 *   Q  Quality    품질(Quality) — 요구 수준을 맞췄나
 *   C  Cost       원가(Cost)    — 비용을 얼마나 잘 관리했나
 *   D  Delivery   납기(Delivery) — 원하는 때에 댈 수 있나
 *
 * <b>formula 는 실제로 도는 계산식을 적는다.</b> 교과서 정의를 적어두면 화면 숫자와
 * 맞지 않을 때 사람이 계산기를 두드려보고도 원인을 못 찾는다.
 * 정의와 구현이 다르면 note 에 그 차이를 적는다.
 *
 * ⚠ 미구현 상태 (2026-09) : 아래 PQCD_CATALOG 17개 지표는 전부 정의상
 * "증가율/감소율/절감률/단축률" — 즉 전기(기준연도) 대비 변화율이다.
 * 지금 시스템은 해당 기간의 절대값만 낸다. 기준연도를 저장해두고 그것과 비교하는
 * 구조가 아직 없어서, measured=true 인 항목도 변화율 형태로는 못 낸다.
 * 변화율 계산은 이번 작업 범위 밖이고, 별도로 기준연도 저장 구조를 만들어야 한다.
 *
 * → (2026-09) 메인 대시보드 "PQCD 변화율 게이지" 로 아래 17개 중 4개(시간당 생산량 ·
 * 완제품 불량률 · 재공재고 · 납기 단축률)만 먼저 변화율 형태로 낸다. 상단 KPI 카드 5개
 * (생산완료수량·시간당생산량·완제품불량률·재공재고·제조납기준수율)도 Phase 3(2026-09,
 * 사용자 승인) 부터 같은 방식이다 - 화면에 찍히는 값은 항상 "이번달"이다.
 * Phase 4(2026-10, 사용자 승인) 부터 비교 기준(base)을 사람이 고르는 "기준 연도" 셀렉터를
 * 없애고 항목마다 자동으로 고른다: 작년(1~12월) 에 데이터가 있으면 작년 월평균
 * ('lastYear' 모드), 작년에 데이터가 전혀 없으면 지난달로 끝나는 3개월의 월평균
 * ('recent3' 모드)이다. "전기(기준연도) 대비"라는 원래 정의와 완전히 같진 않지만(진짜
 * "작년 이맘때 이 시점까지" 같은 절편 비교는 아니고 작년 전체 월평균과 비교한다), 항목별로
 * 가장 안정적인 비교 기준을 자동으로 고른 절충안이다.
 * 계산 로직은 src/lib/pqcdTrend.js(chooseBaseline 이 모드를 정한다), 데이터 로딩은
 * src/lib/usePqcdData.js, 게이지 컴포넌트는 src/components/dashboard/PqcdGauge.js 참고.
 * 나머지 12개(카드 5개로 옮긴 것 제외)는 여전히 미구현이다.
 */

// 카테고리 색은 상태색(초록=좋음, 주황=주의, 빨강=나쁨, 회색=데이터 없음)과 겹치지 않게 고른다.
// 예전엔 Q 가 게이지의 "좋아짐" 초록(#2e7d32)과 같은 색이라 Q 배지가 상태처럼 읽혔고,
// C 주황은 주의색과 겹쳤다. 초록·주황·빨강·회색 계열은 여기 쓰지 말 것.
export const PQCD = {
  P: { key: 'P', label: '생산', color: '#1976d2' }, // 파랑
  Q: { key: 'Q', label: '품질', color: '#5e35b1' }, // 남보라(인디고-바이올렛)
  C: { key: 'C', label: '원가', color: '#00838f' }, // 청록
  D: { key: 'D', label: '납기', color: '#546e7a' }, // 청회색
};

/** 메인 대시보드 카드에 붙는 설명. 키는 카드가 직접 참조한다. */
export const METRIC_HELP = {
  producedQty: {
    category: 'P',
    title: '생산량',
    desc: '값은 이번달(1일~오늘) 생산 완료된 제품 수량이고, 아래 화살표는 비교 기준(작년 '
        + '데이터가 있으면 작년 월평균, 없으면 직전 3개월 월평균) 대비 증감률입니다.',
    formula: '이번달 값 = 계획별로 완료수량(CMPL_QTY)이 있으면 그 값을, 없으면 생산수량(PROD_QTY)을, '
        + '그것도 없으면 생산실적(MANU_PRODUCTION_RESULT)의 생산수량 합계를 써서 합산 '
        + '(계획 상태가 생산완료·출하인 계획만 대상)',
    note: '계획 상태가 생산완료(PRODUCED) 또는 출하(SHIPPED)인 계획만 셉니다 — 진행 중인 계획과 '
        + '완제품 불합격(QC_FAILED) 계획(전량 폐기)은 생산량에 들어가지 않습니다. '
        + '비교 기준(base)은 항목별로 자동입니다 - 작년(1~12월) 에 데이터가 있으면 작년 '
        + '월평균, 작년에 데이터가 전혀 없으면 지난달로 끝나는 3개월의 월평균입니다. 어느 쪽이든 '
        + '실적이 없는(행 자체가 없는) 달은 0으로 치지 않고 평균에서 뺍니다.',
  },

  goodRate: {
    category: 'Q',
    title: '양품률',
    desc: '검사를 실시한 공정 중 불량이 아니었던 비율입니다. 높을수록 좋습니다.',
    formula: '100 − 공정 불량률',
    note: '공정 불량률과 같은 모집단(검사를 실시한 공정)을 쓰기 때문에 두 값을 더하면 항상 100%가 됩니다. '
        + '다만 화면 표시는 반올림 자릿수가 달라(양품률 소수 1자리, 공정 불량률 소수 2자리) '
        + '99.9%/0.13% 처럼 합이 정확히 100.0이 아닌 것처럼 보일 수 있습니다 - 반올림 전 원래 비율은 항상 100%입니다.',
  },

  processDefectRate: {
    category: 'Q',
    title: '공정 불량률',
    desc: '공정을 진행하면서 검사를 실시한 것 중 불량이 나온 비율입니다. 낮을수록 좋습니다.',
    formula: 'Σ 공정 불량수량 ÷ Σ 공정 생산수량 × 100 (검사를 실시한 공정만)',
    note: '공정마다 "검사했다/안 했다"를 따로 표시합니다. 검사를 실시한 공정만 모집단이고, '
        + '검사를 안 한 공정은 분모·분자 어디에도 들어가지 않습니다 — 안 빼면 분모만 커져서 '
        + '불량률이 실제보다 낮게 나옵니다.',
  },

  finalDefectRate: {
    category: 'Q',
    title: '완제품 불량률',
    desc: '완제품 검사(계획 전체 판정) 중 불합격 판정이 나온 비율입니다. 낮을수록 좋습니다.',
    formula: '완제품 검사 불합격(FAIL) 건수 ÷ 완제품 검사 판정 건수 × 100',
    note: '완제품 검사는 계획 하나를 통째로 합격/불합격 판정하는 것이라 부분 불량이 없습니다. '
        + '그래서 수량이 아니라 "판정 건수" 기준입니다 — 위 공정 불량률과 계산 단위가 달라 두 값을 '
        + '직접 비교할 수 없습니다. 또한 완제품 불합격(QC_FAILED) 판정이 난 계획은 생산량 지표에서는 '
        + '빠지지만(전량 폐기라 생산량으로 안 침), 이 불량률 지표에서는 계속 포함됩니다 — 같은 계획이 '
        + '지표에 따라 다르게 세어지는 것은 의도된 것입니다.',
  },

  otdRate: {
    category: 'D',
    title: '납기준수율 (OTD)',
    desc: '값은 이번달(1일~오늘) 납품 기준 준수율이고, 아래 화살표는 비교 기준(작년 데이터가 '
        + '있으면 작년 월평균, 없으면 직전 3개월 월평균) 대비 증감률입니다.',
    formula: '이번달 값 = 정시 납품건수 ÷ 총 납품건수 × 100',
    note: '약속일은 수주품의서의 납품예정일을 먼저 쓰고, 없으면 계약 완료일 · 생산계획 완료일 순으로 봅니다. '
        + '실제 납품일은 출하 완료일입니다. 출하 이력이 없는 계획은 세지 않습니다. '
        + '비교 기준(base)은 항목별로 자동입니다 - 작년(1~12월) 에 데이터가 있으면 작년 월평균, '
        + '작년에 데이터가 전혀 없으면 지난달로 끝나는 3개월의 월평균입니다(총 납품건수가 0인 달은 '
        + '평균에서 제외).',
  },

  // 상단 KPI 카드 "완제품 불량률" 전용. 위 finalDefectRate(일반 정의, KPI 리포트 화면 등에서
  // 계속 씀)와 내용은 같지만, 카드는 "이번달 값 + 비교 기준 대비 화살표" 를 같이 보여주므로
  // producedQty/otdRate 와 같은 틀로 따로 적는다.
  finalDefectRateCard: {
    category: 'Q',
    title: '완제품 불량률',
    desc: '값은 이번달(1일~오늘) 완제품 불량률이고, 아래 화살표는 비교 기준(작년 데이터가 '
        + '있으면 작년 월평균, 없으면 직전 3개월 월평균) 대비 증감률입니다. 낮을수록 좋습니다.',
    formula: '이번달 값 = 완제품 검사 불합격(FAIL) 건수 ÷ 완제품 검사 판정 건수 × 100',
    note: '완제품 검사는 계획 하나를 통째로 합격/불합격 판정하는 것이라 부분 불량이 없어, 수량이 '
        + '아니라 "판정 건수" 기준입니다. '
        + '비교 기준(base)은 항목별로 자동입니다 - 작년(1~12월) 에 데이터가 있으면 작년 월평균, '
        + '작년에 데이터가 전혀 없으면 지난달로 끝나는 3개월의 월평균입니다(판정 건수가 0인 달은 '
        + '평균에서 제외).',
  },

  utilRate: {
    category: 'P',
    title: '인력 평균 활용률',
    desc: '투입된 인력이 실제 작업에 쓰인 비율입니다. 노동생산성(생산량 ÷ 투입인원)을 보는 밑자료가 됩니다.',
    formula: '집계 건별 활용률의 단순 평균',
  },

  throughput: {
    category: 'P',
    title: '시간당 생산량',
    desc: '값은 이번달(1일~오늘) 작업시간 1시간당 생산한 수량이고, 아래 화살표는 비교 기준(작년 '
        + '데이터가 있으면 작년 월평균, 없으면 직전 3개월 월평균) 대비 증감률입니다. 높을수록 좋습니다.',
    formula: '이번달 값 = Σ 생산수량 ÷ Σ 작업시간',
    note: '여기서 "작업시간"은 생산실적에 입력된 공수가 아니라 공정 착수~완료(ACTUAL_START_DT~ACTUAL_END_DT) '
        + '경과시간의 합계입니다(생산실적 작업시간 입력 화면이 없어 항상 0이라 대신 씁니다). '
        + '경과시간에는 야간·주말·대기 시간이 그대로 포함되기 때문에, 사람이 실제로 붙어 있던 '
        + '공수(man-hour) 기준보다 분모가 크게 나와 이 지표는 실제 생산성보다 낮게(보수적으로) 나옵니다. '
        + '비교 기준(base)은 항목별로 자동입니다 - 작년(1~12월) 에 데이터가 있으면 작년 월평균, '
        + '작년에 데이터가 전혀 없으면 지난달로 끝나는 3개월의 월평균입니다(작업시간이 0인 달은 '
        + '평균에서 제외).',
  },

  manufacturingOtdRate: {
    category: 'D',
    title: '제조 납기 준수율',
    desc: '계획한 완료일까지 실제로 제조를 끝낸 비율입니다. 높을수록 좋습니다.',
    formula: '계획 완료일까지 끝낸 계획 수 ÷ 계획 완료일이 있는 계획 수 × 100',
    note: '위 납기준수율(OTD)과 재는 구간이 다릅니다. OTD 는 "고객에게 약속한 날 갔는가"라서 '
        + '출하완료일로 재고, 이 지표는 그 앞 단계인 "제조가 약속한 날 끝났는가"만 봅니다. '
        + '만들어두고 아직 출하하지 않은 계획도 여기서는 정시로 잡힙니다. '
        + '완료일이 없는 계획은 비교할 기준이 없어 모수에서 빠집니다.',
  },

  // 상단 KPI 카드 "재공재고" 전용. 아래 wipTrend(게이지, "기준 대비 몇 %" 형태의 변화율 값)와
  // 달리 카드는 이번달(오늘) 재공수량 자체를 보여주므로 producedQty 와 같은 틀로 따로 적는다.
  wipQtyCard: {
    category: 'C',
    title: '재공재고',
    desc: '값은 이번달(오늘) 재공재고 수량이고, 아래 화살표는 비교 기준(작년 데이터가 있으면 '
        + '작년 월말 평균, 없으면 최근 3개월 월말 평균) 대비 증감률입니다. 낮을수록 좋습니다.',
    formula: '이번달(오늘) 재공수량 = Σ GREATEST(목표수량 − 출고수량, 0) (그 시점까지 존재한 계획 전체)',
    note: 'KPI_WIP_INF(입력화면 없음) 대신, "납품창고 입고 = 납품완료"(2026-09) 기준으로 그 시점 '
        + '생산계획에서 직접 계산합니다(selectWipSnapshot 참고). '
        + '비교 기준(base)은 항목별로 자동입니다 - 작년(1~12월) 에 데이터가 있으면 작년 12개월의 '
        + '"월말" 스냅샷 평균, 작년에 데이터가 전혀 없으면 최근 3개월의 "월말" 스냅샷 평균입니다. '
        + '그 시점에 존재한 계획이 아예 없던 달(existPlanCnt=0)만 데이터 없음으로 평균에서 빼고, '
        + '재공이 0인 달은(계획은 있었는데 전부 나간 것이므로) 그대로 포함합니다.',
  },

  // ── 메인 대시보드 "PQCD 변화율 게이지" 4종 (2026-09 Phase 3, 2026-10 Phase 4 갱신) ──
  // 넷 다 같은 두 구간을 비교한다.
  //   이번달(current) : 이번달 1일부터 오늘까지 - 항상 고정.
  //   비교 기준(base) : 항목마다 자동으로 고른다(Phase 4, 2026-10 사용자 승인 - "기준 연도"
  //     셀렉터를 없애고 자동화했다).
  //     · 작년(1~12월) 에 데이터가 있으면 'lastYear' 모드 - 작년 중 데이터 있는 달의 월평균.
  //     · 작년에 데이터가 전혀 없으면 'recent3' 모드 - 지난달로 끝나는 3개월의 월평균.
  //   어느 모드든 "base = base 기간 중 데이터 있는 달의 월별 값 단순평균" 이고, 데이터가 없던
  //   (분모 0 또는 행 자체가 없던) 달은 0으로 치지 않고 평균에서 뺀다(src/lib/pqcdTrend.js
  //   chooseBaseline/calcTrend 참고). 상단 KPI 카드 5개도 같은 base 정의를 쓴다.
  throughputTrend: {
    category: 'P',
    title: '시간당 생산량',
    desc: '이번달 시간당 생산량이 비교 기준(base) 대비 몇 %인지 보여줍니다. 높을수록 좋습니다(100% = 기준과 동일).',
    formula: '이번달 (Σ생산수량 ÷ Σ작업시간) ÷ base 월평균 (Σ생산수량 ÷ Σ작업시간) × 100',
    note: '월별 값을 먼저 낸 뒤 평균합니다(base 기간을 한 번에 합쳐서 비율 내지 않음) - 달마다 '
        + '작업시간 표본 크기가 다르면 두 방식 결과가 갈립니다. 작업시간이 0(데이터 없음)인 '
        + '달은 평균에서 제외합니다. base 기간 전체에 데이터가 없으면 "-" 로 표시합니다. '
        + 'base 는 항목별 자동 선택입니다 - 작년에 데이터가 있으면 작년 월평균, 없으면 '
        + '지난달로 끝나는 3개월 월평균입니다.',
  },

  finalDefectTrend: {
    category: 'Q',
    title: '완제품 불량률',
    desc: '이번달 완제품 불량률이 비교 기준(base) 대비 몇 %인지 보여줍니다. 낮을수록 좋습니다(100% = 기준과 동일).',
    formula: '이번달 (Σ불합격건수 ÷ Σ판정건수 × 100) ÷ base 월평균 (Σ불합격건수 ÷ Σ판정건수 × 100) × 100',
    note: '판정건수가 0(데이터 없음)인 달은 평균에서 제외합니다. base 불량률이 0%였다면 '
        + '"0에서 몇 배 늘었나" 자체가 정의되지 않아 "-"(기준 불량률 0%) 로 표시합니다 - '
        + '0으로 나눌 수 없기 때문입니다. base 는 항목별 자동 선택입니다 - 작년에 데이터가 '
        + '있으면 작년 월평균, 없으면 지난달로 끝나는 3개월 월평균입니다.',
  },

  wipTrend: {
    category: 'C',
    title: '재공재고율',
    desc: '이번달(오늘) 재공재고가 비교 기준(base, 월말 스냅샷 평균) 대비 몇 %인지 보여줍니다. 낮을수록 좋습니다(100% = 기준과 동일).',
    formula: '이번달(오늘) 재공수량 ÷ base 월말 재공수량 평균 × 100, '
        + '재공수량 = Σ GREATEST(목표수량 − 출고수량, 0) (그 시점까지 존재한 계획 전체)',
    note: 'KPI_WIP_INF(입력화면 없음) 대신, "납품창고 입고 = 납품완료"(2026-09) 기준으로 그 시점 '
        + '생산계획에서 직접 계산합니다 - 목표수량은 생산완료 이상 상태면 실제 생산수량, 그 외엔 '
        + '계획수량이고(계획보다 적게 만들고 끝난 계획이 영원히 재공으로 안 남게), 출고수량은 '
        + '창고입고 실적과 그 외 출하 실적 중 더 큰 쪽입니다(같은 물량이 창고입고+고객출하 두 '
        + '행으로 중복 기록되는 것을 막기 위함 - selectWipSnapshot 참고). 계획 시작 시점은 계획 '
        + '생성일(CREATED_AT)로 봅니다. base 는 항목별 자동 선택입니다 - 작년에 데이터가 있으면 '
        + '작년 12개월의 "월말" 스냅샷 평균, 없으면 최근 3개월의 "월말" 스냅샷 평균입니다. 그 '
        + '시점에 존재한 계획이 아예 없던 달(existPlanCnt=0 - 아직 시스템을 안 쓰던 시절)만 '
        + '데이터 없음으로 평균에서 빼고, 재공이 0인 달은(계획은 있었는데 전부 나간 것이므로) '
        + '그대로 포함합니다 - 이 점이 다른 세 지표(작업시간·판정건수 0을 "데이터 없음"으로 '
        + '빼는 것)와 다릅니다.',
  },

  deliveryShortenRate: {
    category: 'D',
    title: '납기 단축률',
    desc: '이번달 계약완료일~납품완료일 소요일수가 비교 기준(base)보다 며칠 짧아졌는지를 %로 보여줍니다. '
        + '양수면 단축(개선), 음수면 늘어난 것입니다.',
    formula: '(base 월평균 계약~납품 소요일수 − 이번달 계약~납품 소요일수) ÷ base 월평균 × 100',
    note: '집계 기준월은 납품완료월입니다 - "그 달에 다 나간 건들이 평균 며칠 걸렸나" 를 봅니다. '
        + '"납품완료" 는 계약완료일과 마찬가지로 2026-09부터 "납품창고 입고 = '
        + '납품완료" 기준입니다 - 마지막 납품창고 입고일을 쓰고, 창고입고 기록이 없는 레거시 직송 '
        + '건만 마지막 고객 출하일로 대체합니다. 계약완료일이나 납품완료일이 없는 건, 아직 전량 '
        + '납품되지 않은 건, 납품일이 계약완료일보다 앞선(날짜 입력 오류) 건은 평균에서 뺍니다. '
        + '기존 납기 KPI(OTD, avgLeadDays)와는 재는 구간이 달라 값이 다르게 나올 수 있습니다. '
        + 'base 는 항목별 자동 선택입니다 - 작년에 데이터가 있으면 작년 월평균, 없으면 지난달로 '
        + '끝나는 3개월 월평균입니다.',
  },
};

/**
 * 스마트공장 핵심 성과지표(PQCD) 세부지표 17개.
 *
 * 일반적인 교과서 PQCD 목록이 아니라 실제 사업에서 쓰기로 확정한 17개 세부지표다.
 * measured / note 는 코드를 조사해서 판정한 "지금 산출 가능한가" 결과를 그대로 옮긴 것이다.
 *   measured: true  → 가능(지금 있는 데이터·테이블로 계산할 수 있다)
 *   measured: false → 조건부 또는 불가(부분적으로만 되거나, 필요한 데이터/기능이 아예 없다).
 *                      어느 쪽인지는 note 에 적는다.
 * priority: true(●) 는 최우선 적용 지표.
 *
 * ⚠ 위 파일 상단 주석대로, 아래 formula 는 모두 "해당 기간의 절대값" 계산식이다.
 * 실제 지표 정의(증가율/감소율/절감률/단축률)는 전기 대비 변화율이며 아직 못 낸다.
 */
export const PQCD_CATALOG = [
  // P — 생산(Product)
  { category: 'P', name: '생산품목수', variation: '증가율', formula: '기간 내 생산실적/계획에 등장한 제품 distinct 수', measured: true,
    note: '생산실적·계획 데이터로 셀 수 있음. 다만 이 지표로 낸 적은 없어 집계를 새로 만들어야 함.' },
  { category: 'P', name: '매출액', variation: '증가율', formula: '세금계산서 · 수주 확정분 매출 합계', measured: true,
    note: '세금계산서/수주 확정분으로 낼 수 있음. 다만 "확정매출"을 무엇으로 볼지(세금계산서 발행분? 수주 확정분?) 정의가 먼저 필요.' },
  { category: 'P', name: '영업이익률', variation: '증가율', formula: '(매출액 − 매출원가 − 판관비) ÷ 매출액 × 100', measured: false,
    note: '조건부. 원가산출의 매출이익금/매출금액으로 매출총이익률까지는 낼 수 있으나 판관비 데이터가 없어 영업이익률은 못 냄.' },
  { category: 'P', name: '시간당 생산량', variation: '증가율', formula: '생산량 ÷ 작업시간', measured: true, priority: true,
    note: '이미 있음 (생산 KPI의 throughput). 다만 "작업시간"은 생산실적 입력값이 아니라 공정 착수~완료 '
        + '경과시간 합계다(생산실적 작업시간 입력 화면이 없어 항상 0이라 대신 씀) - 대기·야간 시간이 섞여 '
        + '실제 공수 기준보다 낮게 나온다.' },
  { category: 'P', name: '제조리드타임', variation: '단축률', formula: '생산계획 실제완료일 − 실제시작일', measured: true, priority: true,
    note: '생산계획 자체의 실제착수일은 비어 있어(코드 미구현) 못 씀. 대신 라우팅 상세(공정별 실제착수/완료일)의 '
        + '계획 내 최단 착수일~최장 완료일로 계산. 다만 "시작" 없이 바로 "완료" 처리하는 입력 습관 탓에 '
        + '착수일=완료일(0일)인 건이 대부분이라, 지금 나오는 평균은 실제 제조기간보다 짧다.' },
  { category: 'P', name: '설비가동률', variation: '향상률', formula: '설비 실가동시간 ÷ 계획가동시간 × 100', measured: true,
    note: '이미 있음 (설비 KPI의 operRate).' },

  // Q — 품질(Quality)
  { category: 'Q', name: '공정불량률', variation: '감소율', formula: 'Σ 공정 불량수량 ÷ Σ 공정 생산수량 × 100 (검사 실시한 공정만)', measured: true, priority: true,
    note: '이번에 신설. 검사를 실시한 공정(QC_YN=Y)만 모집단 — 검사 안 한 공정은 빼야 분모가 부풀지 않음.' },
  { category: 'Q', name: '완제품 불량률', variation: '감소율', formula: '완제품 검사 FAIL 건수 ÷ 완제품 검사 판정 건수 × 100', measured: true, priority: true,
    note: '이번에 신설. 건수 기준 — 완제품 검사는 계획 전체를 한 번에 판정해 부분 불량이 없음.' },
  { category: 'Q', name: '검사불량률', variation: '감소율', formula: '검사 불합격 건수 ÷ 검사 건수 × 100', measured: true,
    note: '완제품 검사 기준으로 재면 위 완제품 불량률과 사실상 같은 값이 됨.' },
  { category: 'Q', name: '반품율', variation: '감소율', formula: '반품 수량 ÷ 출고 수량 × 100', measured: false,
    note: '조건부. 출고구분에 RETURN 코드는 있으나 반품 수량·사유를 별도로 관리하는 구조가 없음.' },
  { category: 'Q', name: 'Claim 건수', variation: '감소율', formula: '월별 클레임 접수 건수', measured: false,
    note: '불가. 클레임 접수 기능 자체가 없음.' },

  // C — 원가(Cost)
  { category: 'C', name: '작업공수', variation: '절감률', formula: '생산실적 작업시간(WORK_HOURS) 합계', measured: false,
    note: 'ProductionResult.WORK_HOURS 는 입력 화면이 없어 항상 0이다 - 이 값으로는 못 낸다. 대신 라우팅 상세 '
        + '(공정별 실제 착수~완료) 경과시간 합계를 임시로 쓴다(생산 KPI의 workHours/throughput). '
        + '경과시간은 야간·주말·대기 시간을 포함해 실제 공수(man-hour)보다 크게 나오므로, 이 지표(작업공수 '
        + '절감)의 정의에 정확히 맞는 값은 아니다 - 진짜 공수 입력 체계가 생기기 전까지의 대체값이다.' },
  { category: 'C', name: '제품원가', variation: '절감률', formula: '재료비 + 노무비 + 경비', measured: false,
    note: '조건부. 원가산출의 구매금액(매입원가)만 있고 재료비·노무비·경비로 분해되어 있지 않음.' },
  { category: 'C', name: '재공재고', variation: '절감률', formula: '공정별 재공(WIP) 수량', measured: true, priority: true,
    note: 'KpiWipDto 로 이미 구현되어 있음. 다만 대시보드 화면에는 아직 노출되지 않음.' },
  { category: 'C', name: '재고비용', variation: '절감률', formula: '평균재고 × 단가 × 유지비율', measured: false,
    note: '불가. 창고 재고수량은 있으나 단가·유지비율 데이터가 없음.' },

  // D — 납기(Delivery)
  { category: 'D', name: '수주출하 리드타임', variation: '감소율', formula: '출하완료일 − 수주일', measured: true,
    note: '수주일~출하완료일로 계산 가능.' },
  { category: 'D', name: '납기단축', variation: '감소율', formula: '납품예정일 − 실제 출하일', measured: true, priority: true,
    note: '납품예정일 대비 실제 출하일. 기존 OTD 로직을 재활용.' },
];
